import { isBoolean, isEmpty, isNil } from 'lodash-es';
import type { CDLRecordListMeta, RecordListMeta } from './../../../../types/api/records/index';
import type {
  CDLQueryParams,
  MenuQueryParams,
  QueryParams,
  FilterQueryParams,
  StockQueryParams,
  ActivityQueryParams,
} from '@centric-os/types';
import { Builder } from 'builder-pattern';
import useCamelSnakeCase from '../use-camel-to-snake-case';

export default () => {
  const mapCDLQueryParams = (queryParams: QueryParams): CDLQueryParams => {
    const { camelToSnake } = useCamelSnakeCase();

    return Builder<CDLQueryParams>()
      .limit(queryParams.limit)
      .page(queryParams.page)
      .sort_by(camelToSnake(queryParams.sortBy))
      .sort_order(queryParams.sortOrder)
      .filter(queryParams.filter)
      .build();
  };

  const mapCentricOSPaginationMeta = (meta: any): RecordListMeta => {
    const { snakeToCamel } = useCamelSnakeCase();
    const metaType: CDLRecordListMeta = meta;

    return Builder<RecordListMeta>()
      .limit(metaType.limit)
      .page(metaType.page)
      .resultCount(metaType.result_count)
      .totalPages(metaType.total_pages)
      .totalResults(metaType.total_results)
      .sortBy(snakeToCamel(metaType.sort_by))
      .sortOrder(metaType.sort_order)
      .build();
  };

  const mapMenuQueryParams = (queryParams: MenuQueryParams): CDLQueryParams => {
    const { camelToSnake } = useCamelSnakeCase();

    const mapFilter = (queryParams: MenuQueryParams): Object => {
      if (!isNil(queryParams.filter) && !isEmpty(queryParams.filter)) {
        return queryParams.filter;
      }

      const filter: FilterQueryParams = {};
      if (!isNil(queryParams.name) && !isEmpty(queryParams.name)) {
        filter.name = queryParams.name;
      }

      if (!isNil(queryParams.price) && !isEmpty(queryParams.price)) {
        filter.price = queryParams.price;
      }

      if (!isNil(queryParams.posid) && !isEmpty(queryParams.posid)) {
        filter.posid = queryParams.posid;
      }

      if (!isNil(queryParams.barcode) && !isEmpty(queryParams.barcode)) {
        filter.barcode = queryParams.barcode;
      }

      if (!isNil(queryParams.is_active) && !isEmpty(queryParams.is_active)) {
        filter.is_active = queryParams.is_active;
      }

      if (!isNil(queryParams.station) && !isEmpty(queryParams.station)) {
        if (isNil(filter.station)) {
          filter.station = { name: null };
        }

        filter.station.name = queryParams.station;
      }

      if (!isNil(queryParams.tags) && !isEmpty(queryParams.tags)) {
        filter.tags = queryParams.tags;
      }

      if (!isNil(queryParams.created_at) && !isEmpty(queryParams.created_at)) {
        filter.created_at = {
          $more_than: queryParams.created_at.$more_than,
          $less_than: queryParams.created_at.$less_than,
          $between: queryParams.created_at.$between,
        };
      }

      if (!isNil(queryParams.updated_at) && !isEmpty(queryParams.updated_at)) {
        filter.updated_at = {
          $more_than: queryParams.updated_at.$more_than,
          $less_than: queryParams.updated_at.$less_than,
          $between: queryParams.updated_at.$between,
        };
      }

      return filter;
    };

    const filter = mapFilter(queryParams);

    const params: CDLQueryParams = Builder<CDLQueryParams>()
      .limit(queryParams.limit)
      .page(queryParams.page)
      .sort_by(camelToSnake(queryParams.sortBy))
      .sort_order(queryParams.sortOrder)
      .filter(filter)
      .build();

    return params;
  };

  const mapStockQueryParams = (queryParams: StockQueryParams): CDLQueryParams => {
    const { camelToSnake } = useCamelSnakeCase();

    const mapFilter = (queryParams: MenuQueryParams): Object => {
      if (!isNil(queryParams.filter) && !isEmpty(queryParams.filter)) {
        return queryParams.filter;
      }
      const filter: FilterQueryParams = {};

      if (!isNil(queryParams.item_name) && !isEmpty(queryParams.item_name)) {
        filter.item_name = queryParams.item_name;
      }

      if (!isNil(queryParams.brand_id) && !isEmpty(queryParams.brand_id)) {
        filter.brand_id = queryParams.brand_id;
      }
      if (isBoolean(queryParams.draft_is_in_stock)) {
        filter.draft_is_in_stock = queryParams.draft_is_in_stock;
      }
      if (!isEmpty(queryParams.draft_is_in_stock)) {
        filter.draft_is_in_stock = queryParams.draft_is_in_stock === 'true';
      }
      if (!isNil(queryParams.type) && !isEmpty(queryParams.type)) {
        filter.type = queryParams.type;
      }

      return filter;
    };

    const filter = mapFilter(queryParams);

    const params: CDLQueryParams = Builder<CDLQueryParams>()
      .limit(queryParams.limit)
      .page(queryParams.page)
      .sort_by(camelToSnake(queryParams.sortBy))
      .sort_order(queryParams.sortOrder)
      .filter(filter)
      .build();

    return params;
  };

  const mapActivityQueryParams = (queryParams: ActivityQueryParams): CDLQueryParams => {
    const { camelToSnake } = useCamelSnakeCase();

    const mapFilter = (queryParams: ActivityQueryParams): Object => {
      if (!isNil(queryParams.filter) && !isEmpty(queryParams.filter)) {
        return queryParams.filter;
      }
      const filter: FilterQueryParams = {};

      if (!isNil(queryParams.created_at) && !isEmpty(queryParams.created_at)) {
        filter.created_at = {
          $more_than: queryParams.created_at.$more_than,
          $less_than: queryParams.created_at.$less_than,
          $between: queryParams.created_at.$between,
        };
      }
      if (!isNil(queryParams.message) && !isEmpty(queryParams.message)) {
        filter.message = queryParams.message;
      }
      if (!isNil(queryParams.status_type) && !isEmpty(queryParams.status_type)) {
        filter.status_type = queryParams.status_type;
      }
      // Remove below filters after version 3 FF is released
      if (!isNil(queryParams.processId) && !isEmpty(queryParams.processId)) {
        filter.process_id = queryParams.processId;
      }
      if (!isNil(queryParams.status) && !isEmpty(queryParams.status)) {
        filter.status = queryParams.status;
      }
      if (!isNil(queryParams.email) && !isEmpty(queryParams.email)) {
        filter.email = queryParams.email;
      }
      return filter;
    };

    const filter = mapFilter(queryParams);

    const params: CDLQueryParams = Builder<CDLQueryParams>()
      .limit(queryParams.limit)
      .page(queryParams.page)
      .sort_by(camelToSnake(queryParams.sortBy))
      .sort_order(queryParams.sortOrder)
      .filter(filter)
      .build();

    return params;
  };

  return {
    mapCDLQueryParams,
    mapCentricOSPaginationMeta,
    mapMenuQueryParams,
    mapStockQueryParams,
    mapActivityQueryParams,
  };
};
